import { useParams } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { GradientLayout } from "../common/DefaultLayout";
import LandingHero from "./LandingHero";
import { LandingSection } from "./LandingSection";
import { useCartStorage } from "../../store";
import { useMemo, useState } from "react";
import { createApplicationFetchOptions, useApplications } from "./hooks/useApplications";
import { PaginatedApplicationsList } from "./PaginatedApplicationsList";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { useCategory } from "../categories/hooks/useCategories";
import { CollectionDetails } from "../collections/CollectionDetails";
import { FilterDropdown } from "../common/FilterDropdown";
import { getEnabledChains } from "../../app/chainConfig";
import { useIpfsCollection } from "../collections/hooks/useCollections";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const FILTER_OPTIONS = [{
  label: "Network",
  children: getEnabledChains().map(_ref => {
    let {
      id,
      name
    } = _ref;
    return {
      label: `Projects on ${name}`,
      value: {
        type: "chain",
        chainId: id
      }
    };
  }),
  allowMultiple: true
}];
export function createCartProjectFromApplication(application) {
  return {
    anchorAddress: application.anchorAddress,
    projectRegistryId: application.projectId,
    roundId: application.roundId,
    chainId: application.chainId,
    grantApplicationId: application.roundApplicationId,
    recipient: application.payoutWalletAddress,
    grantApplicationFormAnswers: [],
    status: "APPROVED",
    applicationIndex: Number(application.roundApplicationId),
    projectMetadata: {
      title: application.name,
      description: application.summaryText,
      bannerImg: application.bannerImageCid,
      logoImg: application.logoImageCid
    },
    amount: ""
  };
}
function createCompositeRoundApplicationId(application) {
  return [application.chainId, application.roundId.toLowerCase(), application.roundApplicationId.toLowerCase()].join(":");
}
function urlParamsToFilterList(urlParams) {
  const chainIds = urlParams.getAll("chainId");
  return chainIds.map(chainId => ({
    type: "chain",
    chainId: Number(chainId)
  }));
}
function filterListToUrlParams(filters) {
  return filters.reduce((acc, filter) => {
    if (filter.type === "chain") {
      acc.append("chainId", String(filter.chainId));
    }
    return acc;
  }, new URLSearchParams());
}
export function ExploreApplicationsPage() {
  var _urlParams$get, _urlParams$get2;
  const [urlParams, setUrlParams] = useSearchParams();
  const {
    collectionCid
  } = useParams();
  const [filters, setFilters] = useState(urlParamsToFilterList(urlParams));
  const category = useCategory(urlParams.get("categoryId"));
  const collection = useIpfsCollection(collectionCid);
  const [searchInput, setSearchInput] = useState((_urlParams$get = urlParams.get("q")) !== null && _urlParams$get !== void 0 ? _urlParams$get : "");
  const [searchQuery, setSearchQuery] = useState((_urlParams$get2 = urlParams.get("q")) !== null && _urlParams$get2 !== void 0 ? _urlParams$get2 : "");
  const isPreloading = category.isLoading || collection.isLoading;
  const preloadingError = category.error || collection.error;
  const applicationsFetchOptions = isPreloading || preloadingError ? null : createApplicationFetchOptions({
    searchQuery,
    category: category.data,
    collection: collection.data,
    filters
  });
  const {
    applications,
    applicationMeta,
    totalApplicationsCount,
    isLoading: applicationsLoading,
    isLoadingMore,
    loadNextPage,
    hasMorePages,
    error: applicationsError
  } = useApplications(isPreloading || preloadingError ? null : applicationsFetchOptions);
  const isLoading = isPreloading || applicationsLoading;
  const error = preloadingError || applicationsError;
  const allSemantic = applicationMeta.every(item => item.searchType === "semantic");
  const {
    projects,
    add,
    remove
  } = useCartStorage();
  const applicationIdsInCart = useMemo(() => {
    return new Set(projects.map(project => [project.chainId, project.roundId, project.grantApplicationId].join(":")));
  }, [projects]);
  function addApplicationToCart(application) {
    const cartProject = createCartProjectFromApplication(application);
    add(cartProject);
  }
  function removeApplicationFromCart(application) {
    const cartProject = createCartProjectFromApplication(application);
    remove(cartProject);
  }
  function applicationExistsInCart(application) {
    return applicationIdsInCart.has(createCompositeRoundApplicationId(application));
  }
  let pageTitle = "All projects";
  if (searchQuery.length > 0) {
    pageTitle = "Search results";
  } else if (category.data !== undefined) {
    pageTitle = category.data.name;
  } else if (collection.data !== undefined) {
    var _collection$data$name;
    pageTitle = (_collection$data$name = collection.data.name) !== null && _collection$data$name !== void 0 ? _collection$data$name : "Collection";
  }
  const onQueryChange = e => {
    const q = e.target.value;
    setSearchInput(q);
  };
  function onSearchSubmit(e) {
    e.preventDefault();
    setSearchQuery(searchInput);
    setUrlParams(`?q=${searchInput}`);
  }
  function onFiltersChange(newFilters) {
    setFilters(newFilters);
    setUrlParams(`?${filterListToUrlParams(newFilters).toString()}`);
  }
  return /*#__PURE__*/_jsxs(GradientLayout, {
    showWalletInteraction: true,
    children: [/*#__PURE__*/_jsx(LandingHero, {}), collection.data && /*#__PURE__*/_jsx(CollectionDetails, {
      collection: collection.data,
      projectsInView: applications.length,
      onAddAllApplicationsToCart: () => applications.forEach(addApplicationToCart)
    }), /*#__PURE__*/_jsxs(LandingSection, {
      title: collection ? "" : isLoading ? "Loading..." : `${pageTitle} (${totalApplicationsCount})`,
      action: collection && /*#__PURE__*/_jsxs("div", {
        className: "font-mono flex gap-x-4 items-center w-full",
        children: [/*#__PURE__*/_jsxs("form", {
          className: "relative",
          onSubmit: onSearchSubmit,
          onBlur: onSearchSubmit,
          "data-ph-capture-attribute-search-query": searchInput,
          children: [/*#__PURE__*/_jsx(MagnifyingGlassIcon, {
            width: 22,
            height: 22,
            className: "text-white absolute left-[14px] top-[10px]"
          }), /*#__PURE__*/_jsx("input", {
            type: "text",
            name: "query",
            placeholder: "Search...",
            onChange: onQueryChange,
            value: searchInput,
            className: "w-full sm:w-96 border-2 border-white rounded-3xl px-4 py-2 mb-2 sm:mb-0 bg-white/50 pl-12 focus:border-white focus:ring-0 text-black font-mono"
          })]
        }), searchQuery.length === 0 && /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx("span", {
            className: "mr-2",
            children: "Filter by"
          }), /*#__PURE__*/_jsx(FilterDropdown, {
            onChange: onFiltersChange,
            selected: filters,
            options: FILTER_OPTIONS
          })]
        })]
      }),
      children: [error !== undefined && /*#__PURE__*/_jsx("div", {
        className: "text-center p-4 my-3",
        children: "Something went wrong"
      }), isLoading === false && isLoadingMore === false && applications.length === 0 && collection === undefined && /*#__PURE__*/_jsx("p", {
        children: "Your search did not match any projects. Try again using different keywords."
      }), isLoading === false && applicationMeta.length > 0 && allSemantic && !category && /*#__PURE__*/_jsx("p", {
        className: "mt-4 mb-10 text-lg",
        children: "Your search did not match any projects. Try again or feel free to browse through projects similar to your search."
      }), error === undefined && /*#__PURE__*/_jsx("div", {
        className: "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6",
        children: /*#__PURE__*/_jsx(PaginatedApplicationsList, {
          applications: applications,
          isLoading: isLoading,
          isLoadingMore: isLoadingMore,
          hasMorePages: hasMorePages,
          loadNextPage: loadNextPage,
          onAddApplicationToCart: addApplicationToCart,
          onRemoveApplicationFromCart: removeApplicationFromCart,
          applicationExistsInCart: applicationExistsInCart
        })
      })]
    })]
  });
}